// @flow
import _ from "lodash"
import { getValues } from "redux-form"
import { List, Map } from "immutable"

import { calculateRefundBeforeFeeDeductions } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingRefund/components/BookingRefundCalculator/services/helpers"
import { SERVICES } from "mmb/consts/additionalServices"
// eslint-disable-next-line import/no-cycle
import { submitForPayment as submitForPaymentAction } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingPayment/services/paymentActions"
import { mapperCreatedBookingChange } from "mmb/scenes/AccountBooking/components/Booking/services/mapper/services/mapperManageBookingChange"
import { BOOKING_STATUSES, MODALS } from "mmb/consts/booking"
import type {
  CalculatorType,
  MedicalFormData,
  Reason,
  RefundFormDataType,
  StepDirection,
  View,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingRefund/consts/refundConsts"
import type { CalculatorPayload } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingRefund/components/BookingRefundCalculator/records/RefundOption"
import mapperRefundOption from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingRefund/components/BookingRefundCalculator/services/mapperRefundOption"
import {
  assistedRefundSelector,
  bookingRefundDataSelector,
  calculatorDataSelector,
  isCalculatorAvailableSelector,
  isFinalStepSelector,
  isMedicalSelector,
  pathSelector,
  pendingCalculatorSelector,
  refundReasonSelector,
  selectedAffectedFlightsSelector,
  selectedAffectedPassengersSelector,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingRefund/services/selectors"
import { currencySelector } from "common/services/currencies/selectors"
// eslint-disable-next-line import/no-cycle
import * as BookingDuck from "../../../services/index"
import * as bookingSelectors from "../../../services/selector"
import { helpcenterApiUrlSelector } from "common/services/branding/selectors"
import { convertToBool } from "../../../services/transformations/transformations"
import { formatDataToSend, formatDataToSendAssisted, getNewStep } from "./helpers"
import { getArticle } from "common/scenes/HelpCenter/services/helpCenterAPI"
import { getLanguageInfo } from "common/services/staticData/index"
import {
  FORM_NAMES,
  MEDICAL_DOCUMENT_TYPES,
  REASONS,
  STEP_DIRECTION,
  SUGGESTED_ARTICLES,
  VIEWS,
} from "../consts/refundConsts"
import cuckoo from "common/services/cuckoo/cuckoo"
import { cuckooEvents } from "common/consts/enums"
import { getFileNames } from "mmb/scenes/Booking/components/BookingFileUpload/BookingFileUploadHelpers"
import {
  cancelBooking,
  getAssistedRefundPrice,
  getCalculatorData,
  getRefundApplications,
  postAssistedRefund,
  postRefundApplication,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingRefund/services/api"

// CONSTANTS
// ===
const ACTIONS = {
  BOOKING_REFUND__REINITIALIZE_STATE: "BOOKING_REFUND__REINITIALIZE_STATE",
  BOOKING_REFUND__REINITIALIZE_CONTACT_STATE: "BOOKING_REFUND__REINITIALIZE_CONTACT_STATE",
  BOOKING_REFUND__AFFECTED_FLIGHT_CHANGE: "BOOKING_REFUND__AFFECTED_FLIGHT_CHANGE",
  BOOKING_REFUND__AFFECTED_PASSENGER_CHANGE: "BOOKING_REFUND__AFFECTED_PASSENGER_CHANGE",
  BOOKING_REFUND__SELECT_ALL_FLIGHTS_PASSENGERS: "BOOKING_REFUND__SELECT_ALL_FLIGHTS_PASSENGERS",
  BOOKING_REFUND__MEDICAL_CHANGE: "BOOKING_REFUND__MEDICAL_CHANGE",
  BOOKING_REFUND__SUBMIT: "BOOKING_REFUND__SUBMIT",
  BOOKING_REFUND__VIEW_CHANGE: "BOOKING_REFUND__VIEW_CHANGE",
  BOOKING_REFUND__TOGGLE_API_CALL_STATUS: "BOOKING_REFUND__TOGGLE_API_CALL_STATUS",
  BOOKING_REFUND__POST_FORM: "BOOKING_REFUND__POST_FORM",
  BOOKING_REFUND__POST_FORM_REQUEST: "BOOKING_REFUND__POST_FORM_REQUEST",
  BOOKING_REFUND__POST_FORM_SUCCESS: "BOOKING_REFUND__POST_FORM_SUCCESS",
  BOOKING_REFUND__POST_FORM_FAILURE: "BOOKING_REFUND__POST_FORM_FAILURE",
  BOOKING_REFUND__SET_ARTICLES: "BOOKING_REFUND__SET_ARTICLES",
  BOOKING_REFUND__TOGGLE_REFUND_METHODS: "BOOKING_REFUND__TOGGLE_REFUND_METHODS",
  BOOKING_REFUND__GET_APPLICATIONS: "BOOKING_REFUND__GET_APPLICATIONS",
  BOOKING_REFUND__GET_APPLICATIONS_REQUEST: "BOOKING_REFUND__GET_APPLICATIONS_REQUEST",
  BOOKING_REFUND__GET_APPLICATIONS_SUCCESS: "BOOKING_REFUND__GET_APPLICATIONS_SUCCESS",
  BOOKING_REFUND__GET_APPLICATIONS_FAILURE: "BOOKING_REFUND__GET_APPLICATIONS_FAILURE",
  BOOKING_REFUND__SET_REASON: "BOOKING_REFUND__SET_REASON",
  BOOKING_REFUND__SET_PATH: "BOOKING_REFUND__SET_PATH",
  BOOKING_REFUND__CALCULATOR_DATA: "BOOKING_REFUND__CALCULATOR_DATA",
  BOOKING_REFUND__CALCULATOR_DATA_REQUEST: "BOOKING_REFUND__CALCULATOR_DATA_REQUEST",
  BOOKING_REFUND__CALCULATOR_DATA_SUCCESS: "BOOKING_REFUND__CALCULATOR_DATA_SUCCESS",
  BOOKING_REFUND__CALCULATOR_DATA_FAILURE: "BOOKING_REFUND__CALCULATOR_DATA_FAILURE",
  BOOKING_REFUND__CALCULATOR_SET_TYPE: "BOOKING_REFUND__CALCULATOR_SET_TYPE",
  BOOKING_REFUND__GET_ASSISTED_PRICE: "BOOKING_REFUND__GET_ASSISTED_PRICE",
  BOOKING_REFUND__GET_ASSISTED_PRICE_REQUEST: "BOOKING_REFUND__GET_ASSISTED_PRICE_REQUEST",
  BOOKING_REFUND__GET_ASSISTED_PRICE_SUCCESS: "BOOKING_REFUND__GET_ASSISTED_PRICE_SUCCESS",
  BOOKING_REFUND__GET_ASSISTED_PRICE_FAILURE: "BOOKING_REFUND__GET_ASSISTED_PRICE_FAILURE",
  BOOKING_REFUND__GET_INSTANT_DATA: "BOOKING_REFUND__GET_INSTANT_DATA",
  BOOKING_REFUND__GET_INSTANT_DATA_REQUEST: "BOOKING_REFUND__GET_INSTANT_DATA_REQUEST",
  BOOKING_REFUND__GET_INSTANT_DATA_SUCCESS: "BOOKING_REFUND__GET_INSTANT_DATA_SUCCESS",
  BOOKING_REFUND__GET_INSTANT_DATA_FAILURE: "BOOKING_REFUND__GET_INSTANT_DATA_FAILURE",
  BOOKING_REFUND__CALCULATOR__POST_FORM: "BOOKING_REFUND__CALCULATOR__POST_FORM",
  BOOKING_REFUND__CALCULATOR__POST_FORM_REQUEST: "BOOKING_REFUND__CALCULATOR__POST_FORM_REQUEST",
  BOOKING_REFUND__CALCULATOR__POST_FORM_SUCCESS: "BOOKING_REFUND__CALCULATOR__POST_FORM_SUCCESS",
  BOOKING_REFUND__CALCULATOR__POST_FORM_FAILURE: "BOOKING_REFUND__CALCULATOR__POST_FORM_FAILURE",
  BOOKING_REFUND__ASSISTED__POST_FORM: "BOOKING_REFUND__ASSISTED__POST_FORM",
  BOOKING_REFUND__ASSISTED__POST_FORM_REQUEST: "BOOKING_REFUND__ASSISTED__POST_FORM_REQUEST",
  BOOKING_REFUND__ASSISTED__POST_FORM_SUCCESS: "BOOKING_REFUND__ASSISTED__POST_FORM_SUCCESS",
  BOOKING_REFUND__ASSISTED__POST_FORM_FAILURE: "BOOKING_REFUND__ASSISTED__POST_FORM_FAILURE",
}

type RefundFormStateType = Map<string, any> // eslint-disable-line flowtype/no-weak-types

const initialState = Map({
  apiCallFailed: false,
  bid: "",
  currentView: VIEWS.REASON,
  refundMethodsExpanded: false,
  medical: "", // can be "true" or "false"
  calculatorType: "",
  calculatorLoaded: false,
  data: Map({
    affectedFlights: Map(),
    affectedPassengers: Map(),
    reason: null,
  }),
  articles: List.of(),
  loading: false,
  calculatorData: null,
  assistedRefund: null,
  path: "",
})

// eslint-disable-next-line flowtype/no-weak-types
export default function(state: RefundFormStateType = initialState, action: Object) {
  if (!Map.isMap(state)) return initialState
  if (!ACTIONS[action.type]) return state

  switch (action.type) {
    case ACTIONS.BOOKING_REFUND__REINITIALIZE_STATE: {
      return initialState
    }

    case ACTIONS.BOOKING_REFUND__AFFECTED_FLIGHT_CHANGE: {
      const currentVal = state.getIn(["data", "affectedFlights", action.payload.flightID])
      return state.setIn(
        ["data", "affectedFlights", action.payload.flightID],
        _.isUndefined(currentVal) ? true : !currentVal,
      )
    }

    case ACTIONS.BOOKING_REFUND__AFFECTED_PASSENGER_CHANGE: {
      const currentVal = state.getIn(["data", "affectedPassengers", action.payload.passengerID])
      return state.setIn(
        ["data", "affectedPassengers", action.payload.passengerID],
        _.isUndefined(currentVal) ? true : !currentVal,
      )
    }

    case ACTIONS.BOOKING_REFUND__SELECT_ALL_FLIGHTS_PASSENGERS: {
      return state
        .setIn(["data", "affectedFlights"], action.payload.flights)
        .setIn(["data", "affectedPassengers"], action.payload.passengers)
    }

    case ACTIONS.BOOKING_REFUND__MEDICAL_CHANGE:
      return state.set("medical", action.payload.value)

    case ACTIONS.BOOKING_REFUND__POST_FORM_REQUEST:
      return state.set("loading", true)

    case ACTIONS.BOOKING_REFUND__POST_FORM_SUCCESS:
      return state
        .set("currentView", VIEWS.THANKYOU)
        .set("loading", false)
        .set("apiCallFailed", false)

    case ACTIONS.BOOKING_REFUND__POST_FORM_FAILURE:
      return state
        .set("currentView", VIEWS.THANKYOU)
        .set("loading", false)
        .set("apiCallFailed", true)

    case ACTIONS.BOOKING_REFUND__TOGGLE_API_CALL_STATUS:
      return state.set("apiCallFailed", action.payload.status)

    case ACTIONS.BOOKING_REFUND__SUBMIT: {
      const mergedState = _.reduce(
        action.payload.data,
        (acc, value, key: string) => {
          let finalVal = value
          if (key === "proceessType") {
            finalVal = convertToBool(value)
          }
          return acc.setIn(["data", key], finalVal)
        },
        state,
      )

      return mergedState
    }

    case ACTIONS.BOOKING_REFUND__SET_ARTICLES:
      return state.set("articles", action.payload.articles)

    case ACTIONS.BOOKING_REFUND__VIEW_CHANGE:
      return state.set("currentView", action.payload.view)

    case ACTIONS.BOOKING_REFUND__TOGGLE_REFUND_METHODS:
      return state.set("refundMethodsExpanded", !state.get("refundMethodsExpanded"))

    case ACTIONS.BOOKING_REFUND__REINITIALIZE_CONTACT_STATE:
      return state.set("refundMethodsExpanded", false)

    case ACTIONS.BOOKING_REFUND__GET_APPLICATIONS_REQUEST:
    case ACTIONS.BOOKING_REFUND__GET_APPLICATIONS_FAILURE:
      return state.set("pendingApplications", [])

    case ACTIONS.BOOKING_REFUND__GET_APPLICATIONS_SUCCESS:
      return state.set("pendingApplications", action.res)

    case ACTIONS.BOOKING_REFUND__GET_INSTANT_DATA_SUCCESS: {
      const _refundOption = mapperRefundOption(action.res)
      return state.set("calculatorData", _refundOption).set("calculatorLoaded", true)
    }

    case ACTIONS.BOOKING_REFUND__GET_INSTANT_DATA_FAILURE:
      return state.set("calculatorData", null).set("calculatorLoaded", true)

    case ACTIONS.BOOKING_REFUND__GET_ASSISTED_PRICE_REQUEST:
    case ACTIONS.BOOKING_REFUND__GET_ASSISTED_PRICE_FAILURE:
      return state.set("assistedRefund", null)

    case ACTIONS.BOOKING_REFUND__GET_ASSISTED_PRICE_SUCCESS:
      return state.set("assistedRefund", action.res)

    case ACTIONS.BOOKING_REFUND__CALCULATOR_DATA_REQUEST:
      return state.set("loading", true)

    case ACTIONS.BOOKING_REFUND__CALCULATOR_DATA_SUCCESS:
      return state.set("loading", false)

    case ACTIONS.BOOKING_REFUND__SET_REASON:
      return state
        .setIn(["data", "reason"], action.payload.reason)
        .set("path", action.payload.reason)

    case ACTIONS.BOOKING_REFUND__SET_PATH:
      return state.set("path", action.payload.path)

    case ACTIONS.BOOKING_REFUND__CALCULATOR_SET_TYPE:
      return state.set("calculatorType", action.payload.type)

    case ACTIONS.BOOKING_REFUND__CALCULATOR__POST_FORM_REQUEST:
      return state.set("loading", true)

    case ACTIONS.BOOKING_REFUND__CALCULATOR__POST_FORM_SUCCESS:
      return state
        .set("currentView", VIEWS.THANKYOU)
        .set("loading", false)
        .set("apiCallFailed", false)

    case ACTIONS.BOOKING_REFUND__CALCULATOR__POST_FORM_FAILURE:
      return state
        .set("currentView", VIEWS.THANKYOU)
        .set("loading", false)
        .set("apiCallFailed", true)

    case ACTIONS.BOOKING_REFUND__ASSISTED__POST_FORM_REQUEST:
      return state.set("loading", true)

    case ACTIONS.BOOKING_REFUND__ASSISTED__POST_FORM_SUCCESS:
      return state
        .set("currentView", VIEWS.THANKYOU)
        .set("loading", false)
        .set("apiCallFailed", false)

    case ACTIONS.BOOKING_REFUND__ASSISTED__POST_FORM_FAILURE:
      return state
        .set("currentView", VIEWS.THANKYOU)
        .set("loading", false)
        .set("apiCallFailed", true)

    default:
      return state
  }
}

// ACTION CREATORS
// ===

/* eslint-disable flowtype/no-weak-types */
export const affectedFlightsChangeAction = (flightID: string, track: boolean = true) => (
  dispatch: Function,
) => {
  if (track) cuckoo.track(cuckooEvents.MMB_REFUNDS_PICK_FLIGHT)
  dispatch({
    type: ACTIONS.BOOKING_REFUND__AFFECTED_FLIGHT_CHANGE,
    payload: {
      flightID,
    },
  })
}

/* eslint-disable flowtype/no-weak-types */
export const affectedPassengersChangeAction = (passengerID: number, track: boolean) => (
  dispatch: Function,
) => {
  if (track) cuckoo.track(cuckooEvents.MMB_REFUNDS_PICK_PASSENGER)
  dispatch({
    type: ACTIONS.BOOKING_REFUND__AFFECTED_PASSENGER_CHANGE,
    payload: {
      passengerID,
    },
  })
}

export const medicalChangeAction = (value: string) => ({
  type: ACTIONS.BOOKING_REFUND__MEDICAL_CHANGE,
  payload: {
    value,
  },
})

export const toggleRefundMethodsAction = () => ({
  type: ACTIONS.BOOKING_REFUND__TOGGLE_REFUND_METHODS,
})

export const destroyRefundFormAction = (form: string) => ({
  type: "redux-form/DESTROY",
  form,
})

export const reinitializeStateAction = () => ({
  type: ACTIONS.BOOKING_REFUND__REINITIALIZE_STATE,
})

export const reinitializeContactStateAction = () => ({
  type: ACTIONS.BOOKING_REFUND__REINITIALIZE_CONTACT_STATE,
})

/* eslint-disable flowtype/no-weak-types */
export const setReasonAction = (reason: Reason) => (dispatch: Function) => {
  cuckoo.track(cuckooEvents.MMB_REFUNDS_CHOOSE_REASON, {
    reason,
  })
  dispatch({
    type: ACTIONS.BOOKING_REFUND__SET_REASON,
    payload: {
      reason,
    },
  })
}

const setPathAction = (path: string) => ({
  type: ACTIONS.BOOKING_REFUND__SET_PATH,
  payload: {
    path,
  },
})

export const setCalculatorTypeAction = (type: CalculatorType) => (dispatch: Function) => {
  cuckoo.track(cuckooEvents.MMB_SET_CALCULATOR_TYPE, {
    type,
  })
  dispatch({
    type: ACTIONS.BOOKING_REFUND__CALCULATOR_SET_TYPE,
    payload: {
      type,
    },
  })
}

/* eslint-disable flowtype/no-weak-types */
export const setInitialStateAction = () => (dispatch: Function) => {
  dispatch(reinitializeStateAction())
  dispatch(destroyRefundFormAction(FORM_NAMES.GENERAL))
  dispatch(destroyRefundFormAction(FORM_NAMES.MEDICAL))
  dispatch(destroyRefundFormAction(FORM_NAMES.MEDICAL_CONTAINER))
  dispatch(destroyRefundFormAction(FORM_NAMES.CONTACT))
}

export const getInstantRefundDataAction = () => async (
  dispatch: Dispatch<*>,
  getState: () => any,
) => {
  const state = getState()
  const bid = bookingSelectors.bookingDataSelector(state).id

  const ok = dispatch({
    type: ACTIONS.BOOKING_REFUND__GET_INSTANT_DATA,
    promise: getCalculatorData(bid),
    _track: "@res",
  })

  return ok
}

export const getAssistedRefundPriceAction = () => async (
  dispatch: Dispatch<*>,
  getState: () => any,
) => {
  const state = getState()
  const bid = bookingSelectors.bookingDataSelector(state).id
  const currency = currencySelector(state)

  const ok = dispatch({
    type: ACTIONS.BOOKING_REFUND__GET_ASSISTED_PRICE,
    promise: getAssistedRefundPrice(bid, currency.toUpperCase()),
    _track: "@res",
  })

  return ok
}

/* eslint-disable flowtype/no-weak-types */
export const getRefundApplicationsAction = () => async (dispatch: Function, getState: Function) => {
  const state = getState()
  const bid = bookingSelectors.bookingDataSelector(state).id

  const ok = dispatch({
    type: ACTIONS.BOOKING_REFUND__GET_APPLICATIONS,
    promise: getRefundApplications(bid),
  })

  return ok
}

export const getCalculatorDataAction = () => async (dispatch: Dispatch<*>) => {
  const ok = dispatch({
    type: ACTIONS.BOOKING_REFUND__CALCULATOR_DATA,
    promise: Promise.all([
      dispatch(getInstantRefundDataAction()),
      dispatch(getAssistedRefundPriceAction()),
      dispatch(getRefundApplicationsAction()),
    ]),
  })

  return ok
}

export const changeViewAction = (view: View) => async (dispatch: Function) => {
  if (view === VIEWS.REASON) {
    dispatch(setInitialStateAction())
  } else {
    dispatch({
      type: ACTIONS.BOOKING_REFUND__VIEW_CHANGE,
      payload: {
        view,
      },
    })
  }
}

/* eslint-disable flowtype/no-weak-types */
export const changeStepAction = (direction: StepDirection) => (
  dispatch: Function,
  getState: Function,
) => {
  const state = getState()

  const currentView = state.bookingRefund.get("currentView")
  const reason = refundReasonSelector(state)
  const actualPath = pathSelector(state)

  const nextStep = getNewStep(actualPath, direction)

  if (reason) {
    dispatch(setPathAction(nextStep.path))
  }

  if (
    nextStep.view === VIEWS.GUIDE &&
    (currentView === VIEWS.GENERAL || currentView === VIEWS.MEDICAL)
  ) {
    dispatch(destroyRefundFormAction(FORM_NAMES.GENERAL))
    dispatch(destroyRefundFormAction(FORM_NAMES.MEDICAL))
    dispatch(destroyRefundFormAction(FORM_NAMES.MEDICAL_CONTAINER))
    dispatch(setReasonAction(REASONS.CANCELLATION_GUIDE))
  }

  if (nextStep.view === VIEWS.GUIDE && currentView === VIEWS.MEDICAL) {
    dispatch(destroyRefundFormAction(FORM_NAMES.MEDICAL))
  }

  if (nextStep.view === VIEWS.GENERAL && currentView === VIEWS.CONTACT) {
    dispatch(reinitializeContactStateAction())
    dispatch(destroyRefundFormAction(FORM_NAMES.CONTACT))
  }

  dispatch(changeViewAction(nextStep.view))

  cuckoo.track(cuckooEvents.MMB_REFUNDS_CONTINUE)

  if (direction === STEP_DIRECTION.PREV) {
    cuckoo.track(cuckooEvents.MMB_REFUNDS_BACK)
  }
}

/* eslint-disable flowtype/no-weak-types */
export const setupRefundMedicalFormAction = () => (dispatch: Function, getState: Function) => {
  // $FlowFixMe
  const reasons: Array<MedicalFormData> = Object.values(getState().form[FORM_NAMES.MEDICAL])
  const medicalContainer = getState().form[FORM_NAMES.MEDICAL_CONTAINER]
  const documents = []

  let files = []
  reasons.forEach((form: MedicalFormData) => {
    // $FlowFixMe
    Object.values(MEDICAL_DOCUMENT_TYPES).forEach((fileType: string) => {
      if (form[fileType].length > 0 && form[fileType][0].value) {
        // $FlowFixMe
        documents.push({ type: fileType, pid: form.id.value })
      }
    })

    const formFiles = _.concat(
      form.medical_files,
      form.death_files,
      form.relative_relationship_files,
    )

    files = _.concat(files, formFiles)
  })

  files.forEach((file, index) => {
    if (!file.value) files.splice(index, 1)
  })

  dispatch({
    type: ACTIONS.BOOKING_REFUND__SUBMIT,
    payload: {
      data: {
        reason: REASONS.MEDICAL,
        files: files.map(x => x.value),
        documents,
        ...getValues(medicalContainer),
      },
    },
  })

  dispatch(changeStepAction(STEP_DIRECTION.NEXT))

  cuckoo.track(cuckooEvents.MMB_REFUNDS_MEDICAL_SUBMIT, {
    forms: getValues(reasons),
    ...getValues(medicalContainer),
  })
}

// eslint-disable-next-line
export const postRefundFormAction = () => async (dispatch: Function, getState: Function) => {
  const state = getState()

  const bid = bookingSelectors.bookingDataSelector(state).id
  const bookingRefundPreferences = bookingRefundDataSelector(state)
  const formattedDataToSend = await formatDataToSend({
    bid,
    ...bookingRefundPreferences.toJS(),
  })

  dispatch({
    type: ACTIONS.BOOKING_REFUND__POST_FORM,
    promise: postRefundApplication(bid, formattedDataToSend),
    _track: formattedDataToSend,
  })

  cuckoo.track(cuckooEvents.MMB_REFUNDS_RAF_SUBMIT, {
    ...formattedDataToSend,
    files: getFileNames(bookingRefundPreferences.toJS().files),
  })
}

/* eslint-disable flowtype/no-weak-types */
export const submitFormAction = (data: RefundFormDataType) => (
  dispatch: Function,
  getState: Function,
) => {
  dispatch({
    type: ACTIONS.BOOKING_REFUND__SUBMIT,
    payload: {
      data,
    },
  })

  if (isFinalStepSelector(getState())) {
    dispatch(postRefundFormAction())
  } else {
    dispatch(changeStepAction(STEP_DIRECTION.NEXT))
  }
}

// eslint-disable-next-line flowtype/no-weak-types
export const closeRefundFormAction = (reloadBooking: boolean = true) => async (
  dispatch: Function,
  getState: Function,
) => {
  const state = getState()

  const bid = bookingSelectors.bookingDataSelector(state).id

  // close the modal
  dispatch(BookingDuck.closeModal())
  dispatch(setInitialStateAction())
  cuckoo.track(cuckooEvents.MMB_REFUNDS_CLOSE)
  if (reloadBooking) dispatch(BookingDuck.reloadBooking(bid))
}

// eslint-disable-next-line flowtype/no-weak-types
export const getSuggestedArticlesAction = () => async (dispatch: Function, getState: Function) => {
  const state = getState()

  const reason = refundReasonSelector(state)

  if (reason) {
    const helpcenterApiUrl = helpcenterApiUrlSelector(state)
    const result = await Promise.all(
      SUGGESTED_ARTICLES[reason].map(id =>
        getArticle(helpcenterApiUrl, id, getLanguageInfo(getState())),
      ),
    )

    const articles = result.reduce((acc, x) => acc.push({ id: x.id, title: x.title }), List())

    dispatch({
      type: ACTIONS.BOOKING_REFUND__SET_ARTICLES,
      payload: {
        articles,
      },
    })
  }
}

export const postCalculatorFormAction = (payload: CalculatorPayload) => async (
  dispatch: Function,
  getState: Function,
) => {
  const state = getState()
  const refundOption = calculatorDataSelector(state)
  const bid = bookingSelectors.bookingDataSelector(state).id

  const { type, cancellationFee, totalAmount, refundableUntil, refundableOptions } = refundOption

  const track = {
    type,
    method: payload.method,
    cancellationFee,
    totalRefund: totalAmount && calculateRefundBeforeFeeDeductions(totalAmount, cancellationFee),
    totalRefundAfterFeeDeduction: totalAmount,
    refundableUntil: refundableUntil && refundableUntil.toString(),
    refundMethodDetails: refundableOptions && _.omit(refundableOptions[payload.method], ["token"]),
  }

  cuckoo.track(cuckooEvents.MMB_CALCULATOR_SUBMIT, track)

  const ok = await dispatch({
    type: ACTIONS.BOOKING_REFUND__CALCULATOR__POST_FORM,
    promise: cancelBooking(bid, payload),
    _track: {
      bid,
      ...track,
    },
  })

  if (ok) {
    dispatch(BookingDuck.reloadBooking(bid, false))
  }

  return ok
}

// eslint-disable-next-line flowtype/no-weak-types
export const resolveAssistedRefund = (serviceForPayment: Object) => async (
  dispatch: Function,
  getState: Function,
) => {
  const state = getState()
  const bid = bookingSelectors.bookingDataSelector(state).id

  const { payload } = serviceForPayment
  const formattedDataToSend = formatDataToSendAssisted(payload)
  cuckoo.track(cuckooEvents.MMB_REFUNDS_ASSISTED_SUBMIT, formattedDataToSend)

  const promise = postAssistedRefund(bid, formattedDataToSend)

  const ok = await dispatch({
    type: ACTIONS.BOOKING_REFUND__ASSISTED__POST_FORM,
    promise,
    _track: serviceForPayment,
  })

  if (ok) {
    if (serviceForPayment.price) {
      dispatch(reinitializeStateAction())
    }

    return promise.then(res => res).then(mapperCreatedBookingChange)
  }

  promise.catch(err => {
    console.warn(err)
    dispatch(BookingDuck.openModal(MODALS.REFUND))
  })

  return ok
}

export const postAssistedRefundFormAction = (data: Object) => async (
  dispatch: Function,
  getState: Function,
) => {
  const state = getState()
  const bid = bookingSelectors.bookingDataSelector(state).id
  const bookingRefundPreferences = bookingRefundDataSelector(state)
  const assistedRefundData = assistedRefundSelector(state)

  dispatch({
    type: ACTIONS.BOOKING_REFUND__SUBMIT,
    payload: {
      data,
    },
  })

  const payload = {
    category: SERVICES.ASSISTED_REFUND,
    bid,
    price: assistedRefundData.price.amount,
    currency: assistedRefundData.price.currency,
    orderAction: resolveAssistedRefund,
    useBalance: false,
    payload: {
      bid,
      ...data,
      ...bookingRefundPreferences.toJS(),
      price: assistedRefundData.price,
    },
  }

  dispatch(submitForPaymentAction(payload))
}

const selectAllFlightsPassengersAction = () => async (dispatch: Function, getState: Function) => {
  const state = getState()
  const booking = bookingSelectors.bookingDataSelector(state)
  const flights = booking.journey
    .getFlights()
    .reduce((acc, item) => acc.set(item.id, true), new Map())
  const passengers = booking.passengers.reduce((acc, item) => acc.set(item.id, true), new Map())

  dispatch({
    type: ACTIONS.BOOKING_REFUND__SELECT_ALL_FLIGHTS_PASSENGERS,
    payload: {
      flights,
      passengers,
    },
  })
}

export const openCalculatorModalAction = () => async (dispatch: Function) => {
  dispatch(BookingDuck.openModal(MODALS.REFUND))
  await Promise.all([
    dispatch(getCalculatorDataAction()),
    dispatch(selectAllFlightsPassengersAction()),
  ])
  dispatch(changeViewAction(VIEWS.CALCULATOR))
}

export const openCancellationRefundAction = () => async (dispatch: Function) => {
  dispatch(BookingDuck.openModal(MODALS.REFUND))
  dispatch(setReasonAction(REASONS.BOOKING_CANCELLATION))
  dispatch(changeStepAction(STEP_DIRECTION.NEXT))
}

export const openScheduleChangeFromRefundAction = () => async (dispatch: Function) => {
  dispatch(closeRefundFormAction(false))
  dispatch(BookingDuck.openModal(MODALS.SCHEDULE_CHANGE))
}

export const resolveGuideAction = () => (dispatch: Function, getState: Function) => {
  const state = getState()
  const booking = bookingSelectors.bookingDataSelector(state)
  const isCalculatorAvailable = isCalculatorAvailableSelector(state)
  const pendingCalculator = pendingCalculatorSelector(state)
  const isMedical = isMedicalSelector(state)
  const affectedPassengers = selectedAffectedPassengersSelector(state)
  const affectedFlights = selectedAffectedFlightsSelector(state)

  const allPassengers = affectedPassengers.size === booking.passengers.length
  const allFlights = affectedFlights.size === booking.journey.getFlights().size

  cuckoo.track(cuckooEvents.MMB_REFUNDS_GUIDE_SUBMIT, {
    allPassengersSelected: allPassengers,
    allFlightsSelected: allFlights,
    selectedFlights: affectedFlights,
    selectedPassengers: affectedPassengers,
    gracePeriodValidUntil: booking.refundOption ? booking.refundOption.validUntil : null,
    medical: isMedical,
    isCalculatorAvailable,
  })

  // Grace period
  if (booking.refundOption && booking.refundOption.validUntil.isAfter()) {
    dispatch(closeRefundFormAction(false))
    dispatch(BookingDuck.openModal(MODALS.FAST_REFUND))
    return
  }

  // Medical
  if (isMedical === "true") {
    dispatch(setReasonAction(REASONS.MEDICAL))
    dispatch(changeStepAction(STEP_DIRECTION.NEXT))
    return
  }

  // Calculator
  if (
    (isCalculatorAvailable &&
      [BOOKING_STATUSES.CONFIRMED].includes(booking.status) &&
      allFlights &&
      allPassengers) ||
    pendingCalculator
  ) {
    dispatch(changeViewAction(VIEWS.CALCULATOR))
    return
  }

  dispatch(setReasonAction(REASONS.BOOKING_CANCELLATION))
  dispatch(changeStepAction(STEP_DIRECTION.NEXT))
}

export const submitGuideAction = () => async (dispatch: Function, getState: Function) => {
  const state = getState()
  const flightHasDeparted = bookingSelectors.hasJourneyDepartedSelector(state)

  if (!flightHasDeparted) {
    await dispatch(getCalculatorDataAction())
  }
  dispatch(resolveGuideAction())
}
